import React, { useState } from 'react';
import { submitGovernanceAssessment } from '../api/client';

export interface AssessmentFormProps {
  projectId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function AssessmentForm({ projectId, onSuccess, onCancel }: AssessmentFormProps) {
  const [scores, setScores] = useState({
    strategicAlignment: 5,
    businessImpact: 5,
    effortRequired: 5,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleScoreChange = (field: string, value: number) => {
    setScores(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await submitGovernanceAssessment({
        projectId,
        strategicAlignment: scores.strategicAlignment,
        businessImpact: scores.businessImpact,
        effortRequired: scores.effortRequired,
        assessedByEmail: 'committee@example.com',
        assessedByName: 'Committee',
      });

      if (response.success) {
        onSuccess();
      } else {
        setError(response.error || 'Failed to submit assessment');
      }
    } catch (err: any) {
      setError(err.message || 'Error submitting assessment');
    } finally {
      setLoading(false);
    }
  };

  const compositeScore = (
    (scores.strategicAlignment * 0.4 +
     scores.businessImpact * 0.35 +
     (11 - scores.effortRequired) * 0.25)
  ).toFixed(1);

  const isBelowLine =
    parseFloat(compositeScore) >= 7 &&
    scores.strategicAlignment >= 6 &&
    scores.effortRequired <= 5 &&
    scores.businessImpact >= 5;

  return (
    <form onSubmit={handleSubmit} className="assessment-form">
      <h3>Governance Assessment</h3>
      <p className="form-help">Rate on a scale of 1-10 (1=low, 10=high)</p>

      <div className="form-group">
        <label htmlFor="alignment">Strategic Alignment *</label>
        <input
          id="alignment"
          type="range"
          min="1"
          max="10"
          value={scores.strategicAlignment}
          onChange={(e) => handleScoreChange('strategicAlignment', parseInt(e.target.value))}
        />
        <span className="score-display">{scores.strategicAlignment}/10</span>
      </div>

      <div className="form-group">
        <label htmlFor="impact">Business Impact *</label>
        <input
          id="impact"
          type="range"
          min="1"
          max="10"
          value={scores.businessImpact}
          onChange={(e) => handleScoreChange('businessImpact', parseInt(e.target.value))}
        />
        <span className="score-display">{scores.businessImpact}/10</span>
      </div>

      <div className="form-group">
        <label htmlFor="effort">Effort Required *</label>
        <input
          id="effort"
          type="range"
          min="1"
          max="10"
          value={scores.effortRequired}
          onChange={(e) => handleScoreChange('effortRequired', parseInt(e.target.value))}
        />
        <span className="score-display">{scores.effortRequired}/10 (lower is easier)</span>
      </div>

      <div className="score-summary">
        <div className="summary-item">
          <span>Composite Score:</span>
          <strong>{compositeScore}/10</strong>
        </div>
        <div className={`summary-item ${isBelowLine ? 'below-line' : 'above-line'}`}>
          <span>Classification:</span>
          <strong>{isBelowLine ? 'Below-the-Line (Auto-Approve)' : 'Above-the-Line (Escalate)'}</strong>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="form-actions">
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Submitting...' : 'Submit Assessment'}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
}
